import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { Settings } from "../../settings";
import { FEATURES_CONFIG } from "../../featuresConfig";

// Static mode configuration
export const STATIC_CONFIG = {
  widthMultiplier: 1.149,
  heightMultiplier: 1.21,
  verticalOffset: 1,
  horizontalOffset: 0,
};

// Spine mode configuration
export const SPINE_CONFIG = {
  verticalOffset: -0.008,
  horizontalOffset: 0.0,
  widthMultiplier: 1.619,
  heightMultiplier: 1.47,
  frameWidthRatio: 1,
  frameHeightRatio: 1,
};

// Manages spine animation for RoyalCollectReelFront component
class RoyalCollectReelFrontController {
  constructor(
    container,
    symbolWidth,
    symbolHeight,
    dimensions,
    dividerDimensions,
    slotRegistry,
  ) {
    this.container = container;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.dimensions = dimensions;
    this.dividerDimensions = dividerDimensions;
    this.spine = null;

    // SlotRegistry functions for centralized injection
    this._registerSlot = slotRegistry?.registerSlot ?? null;
    this._unregisterSlot = slotRegistry?.unregisterSlot ?? null;

    // Slot container for jackpots injection
    this.jackpotsSlotContainer = null;

    // Internal caches for performance optimization
    this._availableAnimationsCache = null;
    this._dividerCountCache = Settings.REEL_COUNT - 1;

    // Defer spine creation to avoid blocking the main thread
    requestAnimationFrame(() => {
      this.initializeSpineAnimation();
    });
  }

  // Initialize spine and play front animations
  initializeSpineAnimation() {
    this.spine = this.createSpine();

    if (!this.spine) {
      return;
    }

    // VALIDATION: Ensure spine has all required components
    const missingComponents = [];
    if (!this.spine.skeleton) missingComponents.push("skeleton");
    if (!this.spine.state) missingComponents.push("state");
    if (!this.spine.skeleton?.data) missingComponents.push("skeleton.data");
    if (!this.spine.skeleton?.slots) missingComponents.push("skeleton.slots");
    if (!this.spine.skeleton?.bones) missingComponents.push("skeleton.bones");

    if (missingComponents.length > 0) {
      return;
    }

    // Setup spine
    this.spine.position.set(0, 0);
    this.spine.zIndex = this.container.zIndex;
    this.spine.visible = true;

    // Add to container
    this.container.addChild(this.spine);

    // VALIDATION: Final check that spine is properly attached
    if (this.spine.parent !== this.container) {
      return;
    }

    // Invalidate animations cache when spine is reinitialized
    this._availableAnimationsCache = null;

    // Apply scaling to match container dimensions
    this.applySpineScaling();

    // Play the feature_front animation
    this.playAnimation("feature_reel_front", true);

    // Attach slot container for jackpots injection (jackpots_position bone)
    this.attachJackpotsSlot();

    // Attach slot container for respin counter injection (bonus_counter bone)
    this.attachBonusCounterSlot();
  }
  createSpine() {
    try {
      // Check if assets are loaded
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "reel_background-skeleton";
      const atlasAlias = "reel_background-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      // Create independent spine instance
      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      // Verify the spine was created properly
      if (!spine || !spine.skeleton || !spine.state) {
        return null;
      }

      // Verify skeleton has required data
      if (!spine.skeleton.data || !spine.skeleton.data.animations) {
        return null;
      }

      return spine;
    } catch (error) {
      return null;
    }
  }

  // Play animation by name on specified track
  playAnimation(animationName, loop = false, track = 0) {
    if (!this.spine || !this.spine.state) {
      return;
    }

    const animations = this.spine.skeleton.data.animations;
    const animation = animations.find((anim) => anim.name === animationName);

    if (!animation) {
      return;
    }

    this.spine.state.setAnimation(track, animationName, loop);
  }

  // Scale spine to match reel area dimensions
  applySpineScaling() {
    if (!this.spine || !this.symbolHeight || !this.dimensions) {
      return;
    }

    const spineBounds = this.spine.getLocalBounds();
    if (!spineBounds || spineBounds.width <= 0 || spineBounds.height <= 0) {
      requestAnimationFrame(() => {
        this.applySpineScaling();
      });
      return;
    }

    // Target inner frame size that should align with the reel area
    // Use cached divider count
    const dividerWidth = this.dividerDimensions?.width ?? 0;
    // Intentionally reduce by two divider widths (overflow on the sides)
    const targetFrameWidth =
      this.symbolWidth * Settings.REEL_COUNT * SPINE_CONFIG.widthMultiplier +
      dividerWidth * (this._dividerCountCache - 2);
    const targetFrameHeight =
      this.symbolHeight *
      Settings.SYMBOLS_VISIBLE *
      SPINE_CONFIG.heightMultiplier;

    // Artwork-specific frame ratios inside the spine bounds
    const cfg = SPINE_CONFIG;
    const requiredSpineWidth = targetFrameWidth / (cfg.frameWidthRatio || 1);
    const requiredSpineHeight = targetFrameHeight / (cfg.frameHeightRatio || 1);

    const scaleX = requiredSpineWidth / spineBounds.width;
    const scaleY = requiredSpineHeight / spineBounds.height;
    this.spine.scale.set(scaleX, scaleY);

    this.spine.position.set(0, 0);
  }

  // Update dimensions and reapply scaling
  resizeReelFront(
    newSymbolWidth,
    newSymbolHeight,
    newDimensions,
    newDividerDimensions,
  ) {
    this.symbolWidth = newSymbolWidth;
    this.symbolHeight = newSymbolHeight;
    if (newDimensions) {
      this.dimensions = newDimensions;
    }
    if (newDividerDimensions) {
      this.dividerDimensions = newDividerDimensions;
    }
    if (this.spine) {
      this.applySpineScaling();
    }
  }

  // Get list of available animation names
  // Cached for performance - invalidated when spine is reinitialized
  getAvailableAnimations() {
    if (!this.spine?.skeleton?.data?.animations) {
      return [];
    }

    // Check cache validity
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    // Calculate and cache
    const animations = this.spine.skeleton.data.animations.map(
      (anim) => anim.name,
    );
    this._availableAnimationsCache = animations;

    return animations;
  }

  // Attach empty container to jackpots_position slot for jackpots spine injection
  // External display objects (Jackpots spine) can be added as children
  // and will follow the bone's transform during feature_reel_front animations
  attachJackpotsSlot() {
    if (!this.spine || !this.spine.skeleton) return;

    const jackpotsSlot = this.spine.skeleton.findSlot("jackpots_position");
    if (jackpotsSlot) {
      this.jackpotsSlotContainer = new PIXI.Container();
      this.jackpotsSlotContainer.name = "jackpots_slot_content";
      this.spine.addSlotObject("jackpots_position", this.jackpotsSlotContainer);
      // Register with SlotRegistry for centralized access (gated by per-component config)
      if (this._registerSlot && FEATURES_CONFIG.SLOT_REGISTRY.JACKPOTS_INJECTION) {
        this._registerSlot("reelfront.rc.jackpots", this.jackpotsSlotContainer);
      }
    }
  }

  // Attach empty container to bonus_counter slot for respin counter injection
  attachBonusCounterSlot() {
    if (!this.spine || !this.spine.skeleton) return;

    const bonusCounterSlot = this.spine.skeleton.findSlot("bonus_counter");
    if (bonusCounterSlot) {
      this.bonusCounterSlotContainer = new PIXI.Container();
      this.bonusCounterSlotContainer.name = "bonus_counter_slot_content";
      this.spine.addSlotObject("bonus_counter", this.bonusCounterSlotContainer);
      // Register with SlotRegistry for centralized access (gated by per-component config)
      if (this._registerSlot && FEATURES_CONFIG.SLOT_REGISTRY.RESPIN_COUNTER_INJECTION) {
        this._registerSlot("reelfront.rc.bonus_counter", this.bonusCounterSlotContainer);
      }
    }
  }
}

export default RoyalCollectReelFrontController;
